import React, { useEffect, useState, useCallback } from "react";
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Download, BarChart3 } from "lucide-react";
import { BioButton } from "@/components/bio/BioButton";
import { MetricCard } from "@/components/bio/MetricCard";
import { StatusBadge } from "@/components/bio/StatusBadge";
import { EmptyState } from "@/components/bio/EmptyState";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { formatUSD, formatDate } from "@/lib/format";

function isoDaysAgo(n) {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
}

export default function ReportsView() {
    const [schools, setSchools] = useState([]);
    const [school, setSchool] = useState("all");
    const [from, setFrom] = useState(isoDaysAgo(13));
    const [to, setTo] = useState(isoDaysAgo(0));
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/dash/filters").then(({ data }) => { setSchools(data.schools); if (data.schools.length === 1) setSchool(data.schools[0].id); });
    }, []);

    const load = useCallback(() => {
        const params = { date_from: from, date_to: to };
        if (school !== "all") params.school_id = school;
        setData(null);
        api.get("/dash/reports", { params }).then(({ data }) => setData(data));
    }, [from, to, school]);

    useEffect(() => { load(); }, [load]);

    const exportCsv = () => {
        if (!data?.rows?.length) return;
        const header = ["Fecha", "Cafetería", "Producto", "Monto USD", "Método", "Estado"];
        const lines = data.rows.map((r) => [formatDate(r.date), r.cafeteria, r.product, r.amount, r.method, r.status].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","));
        const csv = [header.join(","), ...lines].join("\n");
        const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `biofood_reporte_${from}_${to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-5">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Reportes y analítica</h1>
                    <p className="text-bio-text-secondary mt-1 text-sm">Analiza las ventas por rango de fechas y expórtalas.</p>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                    <Select value={school} onValueChange={setSchool}>
                        <SelectTrigger data-testid="report-school" className="h-11 rounded-bio bg-white border-[1.5px] min-w-[150px]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {schools.length > 1 && <SelectItem value="all">Todos los colegios</SelectItem>}
                            {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Input data-testid="report-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-11 rounded-bio bg-white border-[1.5px] w-[150px]" />
                    <Input data-testid="report-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-11 rounded-bio bg-white border-[1.5px] w-[150px]" />
                    <BioButton data-testid="report-export" variant="secondary" onClick={exportCsv} disabled={!data?.rows?.length}><Download className="h-4 w-4" /> Exportar CSV</BioButton>
                </div>
            </div>

            {!data ? (
                <Skeleton className="h-72 w-full rounded-bio-card" />
            ) : (
                <>
                    <div className="grid sm:grid-cols-3 gap-4">
                        <MetricCard label="Ventas totales" value={formatUSD(data.summary.total)} data-testid="report-kpi-total" />
                        <MetricCard label="Transacciones" value={data.summary.count} data-testid="report-kpi-count" />
                        <MetricCard label="Ticket promedio" value={formatUSD(data.summary.avg_ticket)} data-testid="report-kpi-avg" />
                    </div>

                    <div className="bg-white rounded-bio-card border border-bio-border-subtle shadow-bio-sm p-5">
                        <h3 className="text-sm font-bold mb-4">Ventas por día</h3>
                        {data.series.length ? (
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart data={data.series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF5" vertical={false} />
                                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6B7A99" }} tickFormatter={(d) => d.slice(5)} />
                                    <YAxis tick={{ fontSize: 10, fill: "#6B7A99" }} />
                                    <Tooltip formatter={(v) => formatUSD(v)} />
                                    <Line type="monotone" dataKey="total" stroke="#00508A" strokeWidth={2.5} dot={{ r: 3, fill: "#2A87C8" }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : <EmptyState icon={BarChart3} title="Sin datos en el rango" />}
                    </div>

                    <div className="grid lg:grid-cols-2 gap-6">
                        <div className="bg-white rounded-bio-card border border-bio-border-subtle shadow-bio-sm p-5">
                            <h3 className="text-sm font-bold mb-4">Ventas por categoría</h3>
                            <ResponsiveContainer width="100%" height={220}>
                                <BarChart data={data.by_category} layout="vertical" margin={{ left: 20 }}>
                                    <XAxis type="number" tick={{ fontSize: 10, fill: "#6B7A99" }} />
                                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "#1E3A5F" }} width={80} />
                                    <Tooltip formatter={(v) => formatUSD(v)} />
                                    <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                                        {data.by_category.map((_, i) => <Cell key={i} fill={["#00508A", "#2A87C8", "#1464A0", "#7DCEF4", "#1BC5BD"][i % 5]} />)}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="bg-white rounded-bio-card border border-bio-border-subtle shadow-bio-sm p-5">
                            <h3 className="text-sm font-bold mb-4">Ventas por cafetería</h3>
                            <div className="space-y-3 pt-2">
                                {data.by_cafeteria.map((c) => {
                                    const max = Math.max(...data.by_cafeteria.map((x) => x.total), 1);
                                    return (
                                        <div key={c.name}>
                                            <div className="flex justify-between text-sm mb-1"><span className="font-medium">{c.name}</span><span className="font-bold">{formatUSD(c.total)}</span></div>
                                            <div className="h-2.5 rounded-full bg-bio-bg-alt overflow-hidden"><div className="h-full rounded-full bg-bio-primary" style={{ width: `${(c.total / max) * 100}%` }} /></div>
                                        </div>
                                    );
                                })}
                                {!data.by_cafeteria.length && <EmptyState title="Sin datos" />}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
